"use client";
import useSWR, { type SWRConfiguration } from "swr";
import { EnrichedProgram } from "@/common/course/types";
import { ProgramType } from "@/common/program/types";
import { ProductCardProps } from "@/components/ProductCard/types";
import { useTypedSearchHits } from "@/components/Search/hooks/useTypedSearchHits";

// Constants
const ENRICHMENT_CONFIG = {
  MAX_RETRIES: 3,
  BASE_RETRY_DELAY: 1000,
  API_ENDPOINT: "/api/enrich-programs",
} as const;

// Types
type EnrichPayloadProgram = {
  uuid: string;
  title?: string;
  type?: string;
  externalId?: string;
};

type EnrichmentHookOptions = {
  swrConfig?: SWRConfiguration<EnrichedProgram[], Error>;
  maxRetries?: number;
  baseRetryDelay?: number;
  ignorePricingData?: boolean;
  ignoreSubtitle?: boolean;
};

type EnrichmentError = Error & {
  status?: number;
  code?: string;
};

type UseEnrichedSearchHitsReturn = {
  enrichedHits: EnrichedProgram[];
  isLoading: boolean;
  error: EnrichmentError | undefined;
  mutate: () => Promise<EnrichedProgram[] | undefined>;
};

type EnrichmentValidationOptions = {
  ignorePricingData?: boolean;
  ignoreSubtitle?: boolean;
};

const generateSearchHitsKey = (
  searchHits: ProductCardProps[],
): [string, string] | null => {
  if (!searchHits?.length) {
    return null;
  }

  const hitIds = searchHits
    .map((hit) => hit.uuid || hit.objectId)
    .filter(Boolean)
    .sort();

  if (!hitIds.length) {
    return null;
  }

  return ["enrich-search-hits", hitIds.join(",")];
};

const createBasicEnrichedProgram = (
  hit: ProductCardProps,
): EnrichedProgram => ({
  uuid: hit.uuid || hit.objectId || "",
  title: hit.name,
  subtitle:
    hit.hoverOverlay?.aboutThisProduct ||
    hit.hoverOverlay?.primaryDescription ||
    "",
  type: hit.type as ProgramType,
  marketingUrl: hit.href,
  externalId: hit.objectId || "",
  cardImageUrl: hit.imageUrl,
  partners: Array.isArray(hit.partnerKey)
    ? hit.partnerKey.map((name) => ({ name, logoUrl: "" }))
    : hit.partnerKey
      ? [{ name: hit.partnerKey, logoUrl: "" }]
      : [],
  subjects: hit.subjects
    ? Array.isArray(hit.subjects)
      ? hit.subjects
      : [hit.subjects]
    : [],
});

const isEnrichmentIncomplete = (
  enrichedPrograms: EnrichedProgram[],
  validationOptions: EnrichmentValidationOptions = {
    ignorePricingData: false,
    ignoreSubtitle: false,
  },
): boolean => {
  return enrichedPrograms.some(
    (program) =>
      !program.cardImageUrl ||
      (!validationOptions?.ignoreSubtitle && !program.subtitle) ||
      (!validationOptions?.ignorePricingData && !program.pricingData),
  );
};

const getRetryDelay = (attempt: number, baseDelay: number): number => {
  return baseDelay * Math.pow(2, attempt - 1);
};

const createEnrichmentFetcher = (
  searchHits: ProductCardProps[],
  validationOptions: EnrichmentValidationOptions = {
    ignorePricingData: false,
    ignoreSubtitle: false,
  },
) => {
  return async (): Promise<EnrichedProgram[]> => {
    // Transform search hits to partial programs for API
    const partialPrograms: EnrichPayloadProgram[] = searchHits
      .filter((hit) => hit.uuid || hit.objectId)
      .map((hit) => ({
        uuid: hit.uuid || hit.objectId || "",
        title: hit.name,
        type: hit.type,
        externalId: hit.objectId || "",
      }));

    if (!partialPrograms.length) {
      throw new Error("No valid search hits to enrich");
    }

    const response = await fetch(ENRICHMENT_CONFIG.API_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ partialPrograms }),
    });

    if (!response.ok) {
      const enrichmentError: EnrichmentError = new Error(
        `Failed to enrich programs: ${response.status} ${response.statusText}`,
      );
      enrichmentError.status = response.status;
      enrichmentError.code = "ENRICHMENT_API_ERROR";
      throw enrichmentError;
    }

    const { enrichedPrograms } = await response.json();

    // Check if enrichment returned incomplete data and throw error to trigger SWR retry
    if (isEnrichmentIncomplete(enrichedPrograms, validationOptions)) {
      const incompleteError: EnrichmentError = new Error(
        "Enrichment returned incomplete data - missing pricing, images, or descriptions",
      );
      incompleteError.code = "INCOMPLETE_ENRICHMENT_DATA";
      throw incompleteError;
    }

    return enrichedPrograms;
  };
};

/**
 * Hook to enrich search hits from Algolia with full program/course data using SWR
 * This hook gets hits directly from Algolia via useTypedSearchHits
 */
const useEnrichedSearchHits = (
  options: EnrichmentHookOptions = {
    ignorePricingData: false,
    ignoreSubtitle: false,
  },
): UseEnrichedSearchHitsReturn => {
  // Get hits directly from Algolia - no external state needed
  const { items: searchHits } = useTypedSearchHits();

  const validatedSearchHits =
    searchHits?.filter((hit) => hit.uuid || hit.objectId) || [];

  const swrKey = generateSearchHitsKey(validatedSearchHits);
  const fetcher = swrKey
    ? createEnrichmentFetcher(validatedSearchHits, {
        ignorePricingData: options.ignorePricingData,
        ignoreSubtitle: options.ignoreSubtitle,
      })
    : null;

  const maxRetries = options.maxRetries ?? ENRICHMENT_CONFIG.MAX_RETRIES;
  const baseRetryDelay =
    options.baseRetryDelay ?? ENRICHMENT_CONFIG.BASE_RETRY_DELAY;

  const defaultSwrConfig: SWRConfiguration<EnrichedProgram[], Error> = {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    errorRetryCount: maxRetries,
    errorRetryInterval: baseRetryDelay,
    fallbackData: [], // Return empty array while loading
    // Use exponential backoff for retries
    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
      const enrichmentError = error as EnrichmentError;

      // Don't retry on 4xx errors (client errors) or specific error codes
      if (
        enrichmentError?.status &&
        enrichmentError.status >= 400 &&
        enrichmentError.status < 500
      )
        return;
      if (enrichmentError?.code === "INCOMPLETE_ENRICHMENT_DATA") return; // Don't retry incomplete data

      // Stop retrying after maxRetries attempts
      if (retryCount >= maxRetries) return;

      // Use exponential backoff: baseDelay * 2^retryCount
      const delay = getRetryDelay(retryCount + 1, baseRetryDelay);
      setTimeout(() => revalidate({ retryCount }), delay);
    },
  };

  const swrConfig = {
    ...defaultSwrConfig,
    ...options.swrConfig,
  };

  const { data, error, isLoading, mutate } = useSWR(swrKey, fetcher, swrConfig);

  // Fallback to basic enrichment if SWR fails completely
  const enrichedHits =
    data ||
    (error && validatedSearchHits.length > 0
      ? validatedSearchHits.map(createBasicEnrichedProgram)
      : []);

  return {
    enrichedHits,
    isLoading,
    error: error as EnrichmentError | undefined,
    mutate,
  };
};

export type {
  EnrichmentHookOptions,
  UseEnrichedSearchHitsReturn,
  EnrichmentError,
};

export default useEnrichedSearchHits;
